import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Lock, Play, Pause, MapPin, Bike, Loader2, ChevronRight, ChevronLeft, Volume2, Clock, ArrowRight, Navigation, Share2,
  Check, Store, Captions, CaptionsOff, BookOpen, Route as RouteIcon, Gauge,
} from "lucide-react";
import { unlockRoute, generateTTS, ttsUrl, getSelfGuidedRoutes } from "@/lib/api";
import { getDeviceId, getUnlocked, saveUnlocked, removeUnlocked, remainingText, routeLeg, kmText } from "@/lib/access";
import { useLocationShare } from "@/lib/useLocationShare";
import { NavigationScreen } from "@/components/NavigationScreen";
import { RideSummary } from "@/components/RideSummary";
import { LostButton } from "@/components/LostButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function SelfGuided() {
  const [code, setCode] = useState("");
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const [audioLoading, setAudioLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [unlocked, setUnlocked] = useState([]);
  const [tours, setTours] = useState([]);
  useEffect(() => { getSelfGuidedRoutes().then((rs) => setTours([...rs].sort((a, b) => a.price - b.price))).catch(() => {}); }, []);
  const [imgIdx, setImgIdx] = useState(0);
  const [autoNav, setAutoNav] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [storyDoneId, setStoryDoneId] = useState(null);
  const rideRef = useRef({ startedAt: Date.now(), distanceM: 0, visited: 1 });
  const addDistance = useCallback((d) => { rideRef.current.distanceM += d; }, []);
  useEffect(() => { rideRef.current.visited = Math.max(rideRef.current.visited, active + 1); }, [active]);
  useEffect(() => { if (route) rideRef.current = { startedAt: Date.now(), distanceM: 0, visited: 1 }; }, [route?.id]);
  const [, setTick] = useState(0);
  const audioRef = useRef(null);
  const cacheRef = useRef({});
  const [searchParams, setSearchParams] = useSearchParams();

  useLocationShare(route, active, autoNav);
  useEffect(() => { setUnlocked(getUnlocked()); }, []);
  // Auto-unlock when arriving via a printed QR card (?code=XXXX)
  useEffect(() => {
    const c = searchParams.get("code");
    if (c) {
      setCode(c.toUpperCase());
      doUnlock(c);
      searchParams.delete("code");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // refresh countdowns every 30s
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  const doUnlock = async (rawCode, { silent } = {}) => {
    const c = (rawCode || "").trim();
    if (!c) return toast.error("Enter your map code");
    setLoading(true);
    try {
      const r = await unlockRoute(c, getDeviceId());
      setRoute(r);
      setActive(0);
      const entry = {
        route_id: r.id, title: r.title, cover_image: r.cover_image,
        code: c.toUpperCase(), expires_at: r.access.expires_at, kind: r.access.kind, role: r.access.role, lost_enabled: r.access.lost_enabled,
      };
      saveUnlocked(entry);
      setUnlocked(getUnlocked());
      if (!silent) toast.success(`Unlocked: ${r.title}`);
      toast.info("Your location is shared with Whangamatā Cycles while this map is open — for your health and safety.", { duration: 8000 });
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.detail
        || (status === 403 ? "This code is in use on another device."
          : status === 410 ? "This code has expired."
          : "Invalid code");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const continueMap = async (entry) => {
    setLoading(true);
    try {
      const r = await unlockRoute(entry.code, getDeviceId());
      setRoute(r);
      setActive(0);
      saveUnlocked({ ...entry, expires_at: r.access.expires_at, kind: r.access.kind });
      setUnlocked(getUnlocked());
    } catch (e) {
      removeUnlocked(entry.route_id);
      setUnlocked(getUnlocked());
      toast.error(e?.response?.data?.detail || "That map's access has ended. Grab a fresh code in store.");
    } finally {
      setLoading(false);
    }
  };

  const stop = route?.stops?.[active];

  const stopAudio = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
    setPlaying(false);
  };
  useEffect(() => { stopAudio(); setImgIdx(0); }, [active]);

  // Lock body scroll while the full-screen navigator is open
  useEffect(() => {
    document.body.style.overflow = autoNav ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [autoNav]);

  // Subtitles: split the story into sentences and time them across the audio (proportional to length)
  const [audioT, setAudioT] = useState({ t: 0, d: 0 });
  const [subsOn, setSubsOn] = useState(true);
  const [readStory, setReadStory] = useState(false);
  const sentences = useMemo(() => (stop?.story || "").match(/[^.!?]+[.!?]+["']?|[^.!?]+$/g)?.map((s) => s.trim()).filter(Boolean) || [], [stop?.story]);
  const subtitle = useMemo(() => {
    if (!playing || !audioT.d || !sentences.length) return "";
    const total = sentences.reduce((a, s) => a + s.length, 0);
    const lead = (stop?.name?.length || 0) + 2;
    let acc = 0;
    const frac = audioT.t / audioT.d;
    if (frac < lead / (total + lead)) return stop.name;
    for (const s of sentences) {
      acc += s.length;
      if (frac <= (lead + acc) / (total + lead)) return s;
    }
    return sentences[sentences.length - 1];
  }, [playing, audioT, sentences, stop]);

  const playNarration = async () => {
    if (!stop) return;
    if (playing) { stopAudio(); return; }
    setAudioLoading(true);
    try {
      let url = cacheRef.current[stop.id];
      if (!url) {
        if (stop.audio) {
          url = ttsUrl(stop.audio);
        } else {
          const res = await generateTTS(`${stop.name}. ${stop.story}`);
          url = ttsUrl(res.url);
        }
        cacheRef.current[stop.id] = url;
      }
      if (!audioRef.current) audioRef.current = new Audio();
      audioRef.current.src = url;
      audioRef.current.onended = () => { setPlaying(false); setStoryDoneId(stop.id); };
      audioRef.current.ontimeupdate = () => setAudioT({ t: audioRef.current.currentTime, d: audioRef.current.duration || 0 });
      await audioRef.current.play();
      setPlaying(true);
    } catch {
      toast.error("Could not load narration");
    } finally {
      setAudioLoading(false);
    }
  };

  // Locked / code entry view
  if (!route) {
    return (
      <div className="bg-[#0A0A0A] text-[#F5F5F0] min-h-screen grain">
        {/* Hero */}
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-7">
            <p className="overline text-[#93C5FD] mb-5">Self-Guided Adventures</p>
            <h1 className="font-serif font-light text-4xl sm:text-5xl md:text-6xl leading-[0.95]">Ride your own way.<br />We'll tell the stories.</h1>
            <p className="mt-6 text-base md:text-lg text-[#F5F5F0]/60 max-w-xl leading-relaxed">
              Pick a route, hire a bike (or bring your own) and Echo guides you turn by turn — with a story to listen to at every stop. Your tour code arrives by email and unlocks the map on your phone for 12 hours.
            </p>
            <div className="mt-6 max-w-xl rounded-2xl border border-white/10 bg-white/5 p-4" data-testid="tour-pricing-explainer">
              <p className="text-base text-[#F5F5F0]/85 leading-relaxed">Purchase <strong className="text-white">1 tour at full price</strong> and add group members for just <strong className="text-white">$5 each</strong>. Under-12s ride along free.</p>
            </div>
          </div>
          <div className="md:col-span-5 h-56 md:h-64 rounded-2xl overflow-hidden relative">
            <img src="/brand/photos/p10.jpg" alt="Riders in Whangamatā" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-5 left-5 flex items-center gap-2 text-white"><Bike className="w-5 h-5" /><span className="font-serif text-2xl">Whangamatā, at your pace</span></div>
          </div>
        </div>

        {/* Tours — the main event */}
        <div className="max-w-7xl mx-auto px-6 md:px-10 pb-16" data-testid="tour-overview">
          <div className="flex items-end justify-between gap-4 mb-6">
            <div><p className="overline text-[#F5F5F0]/40">Choose your adventure</p><h2 className="font-serif text-2xl md:text-3xl mt-1">Three routes. One town. Countless stories.</h2></div>
            <span className="hidden sm:inline text-sm text-[#F5F5F0]/40 font-mono">from $25 per rider · + bike hire</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {tours.map((r, i) => (
              <Link
                key={r.id}
                to={`/book?tour=${r.id}`}
                data-testid={`tour-card-${r.id}`}
                className="group rounded-2xl overflow-hidden border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] hover:border-[#93C5FD]/40 transition-colors flex flex-col"
              >
                <div className="relative h-52 overflow-hidden bg-white/10">
                  {r.cover_image && <img src={r.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                  <span className="absolute top-4 left-4 px-2.5 py-1 rounded-full bg-black/50 backdrop-blur text-[11px] font-mono uppercase tracking-wider text-white/80">{r.difficulty}</span>
                  <span className="absolute top-4 right-4 px-2.5 py-1 rounded-full bg-[#F5F5F0] text-[#001A4D] text-xs font-mono font-semibold">${r.price} + bike</span>
                  <p className="absolute bottom-4 left-4 right-4 font-serif text-3xl leading-none">{r.title}</p>
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <p className="text-sm md:text-[15px] text-[#F5F5F0]/70 leading-relaxed whitespace-pre-line">{r.description}</p>
                  <div className="mt-4 flex flex-wrap gap-2 text-[11px] font-mono text-[#F5F5F0]/55">
                    <span className="px-2 py-1 rounded-full bg-white/5 border border-white/10">{r.stops_label || r.stops.length} story stops</span>
                    <span className="px-2 py-1 rounded-full bg-white/5 border border-white/10">{r.distance_km} km</span>
                    <span className="px-2 py-1 rounded-full bg-white/5 border border-white/10">{r.duration_text}</span>
                  </div>
                  <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between gap-3">
                    <span className="text-sm text-[#F5F5F0]/60">Starts & ends at Whangamatā Cycles</span>
                    <span className="inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-[#F5F5F0] text-[#001A4D] text-sm font-medium whitespace-nowrap shrink-0 group-hover:bg-white">Book <ArrowRight className="w-4 h-4" /></span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Unlocked maps on this device */}
        {unlocked.length > 0 && (
          <div className="max-w-7xl mx-auto px-6 md:px-10 pb-12" data-testid="unlocked-maps">
            <p className="overline text-[#F5F5F0]/40 mb-4">Your unlocked maps</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {unlocked.map((u) => (
                <button key={u.route_id} data-testid={`continue-map-${u.route_id}`} onClick={() => continueMap(u)} className="w-full flex items-center gap-4 p-3 rounded-xl border border-white/10 hover:border-white/30 bg-white/5 transition-colors text-left">
                  <div className="w-14 h-14 rounded-lg overflow-hidden bg-white/10 shrink-0">{u.cover_image && <img src={u.cover_image} alt="" className="w-full h-full object-cover" />}</div>
                  <div className="flex-1 min-w-0"><p className="font-serif text-lg truncate">{u.title}</p><p className="text-xs text-[#F5F5F0]/50 flex items-center gap-1.5 font-mono"><Clock className="w-3 h-3" /> {remainingText(u.expires_at)}</p></div>
                  <ArrowRight className="w-5 h-5 text-[#F5F5F0]/40 shrink-0" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Code banner — for riders with a code from store */}
        <div className="border-t border-white/10 bg-[#001A4D]" data-testid="code-banner">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex-1">
              <p className="overline text-[#93C5FD]">Already have a code?</p>
              <p className="font-serif text-2xl mt-1">Enter your tour code to open the map</p>
              <p className="text-sm text-white/60 mt-1">Most riders open their tour straight from the link in their email — this is here if you were given a code in store.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              <Input
                data-testid="unlock-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === "Enter" && doUnlock(code)}
                placeholder="Enter tour code"
                className="h-12 md:w-56 bg-white/10 border-white/20 text-white placeholder:text-white/40 uppercase tracking-widest font-mono"
              />
              <Button data-testid="unlock-btn" onClick={() => doUnlock(code)} disabled={loading} className="h-12 px-8 rounded-full bg-[#F5F5F0] hover:bg-white text-[#001A4D] shrink-0">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Lock className="w-4 h-4 mr-2" /> Unlock</>}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Route player view — story-first, no map (the Navigate screen has the map)
  const gallery = (stop.images && stop.images.length ? stop.images : (stop.image ? [stop.image] : []));
  const mainImg = gallery[Math.min(imgIdx, gallery.length - 1)] || stop.image;
  const isLastStop = active === route.stops.length - 1;
  const visitedMax = rideRef.current.visited - 1;

  return (
    <div className="bg-[#F5F5F0] min-h-screen has-mobile-bar">
      {/* Mobile sticky action bar */}
      {!autoNav && !showSummary && (
        <div data-testid="mobile-action-bar" className="lg:hidden fixed bottom-0 inset-x-0 z-40 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] bg-[#F5F5F0]/90 backdrop-blur border-t border-black/10">
          <div className="flex gap-2">
            <button data-testid="mobile-play-btn" onClick={playNarration} disabled={audioLoading} className="flex-1 h-12 rounded-full bg-[#0A0A0A] text-white font-medium flex items-center justify-center gap-2 disabled:opacity-60">
              {audioLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : playing ? <><Pause className="w-4 h-4" /> Pause</> : <><Play className="w-4 h-4" /> Play story</>}
            </button>
            <button data-testid="mobile-nav-btn" onClick={() => setAutoNav(true)} className="flex-1 h-12 rounded-full bg-[#001A4D] text-white font-medium flex items-center justify-center gap-2">
              <Navigation className="w-4 h-4" /> Navigate
            </button>
          </div>
        </div>
      )}
      {autoNav && (
        <NavigationScreen
          route={route}
          active={active}
          setActive={setActive}
          onExit={() => setAutoNav(false)}
          onFinish={() => { setAutoNav(false); setShowSummary(true); }}
          onDistance={addDistance}
          onPlayStory={playNarration}
          playing={playing}
          storyDoneId={storyDoneId}
          audioLoading={audioLoading}
        />
      )}
      {showSummary && <RideSummary route={route} stats={rideRef.current} onClose={() => setShowSummary(false)} />}

      {/* Hero */}
      <section className="bg-[#001A4D] text-[#F5F5F0] grain" data-testid="route-hero">
        <div className="max-w-6xl mx-auto px-6 md:px-10 pt-10 pb-12 md:pt-14 md:pb-16">
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div className="max-w-2xl">
              <p className="overline text-[#93C5FD] mb-4">Self-Guided Adventure</p>
              <h1 className="font-serif font-light text-4xl sm:text-5xl md:text-7xl leading-[0.95]">{route.title}</h1>
              <div className="flex flex-wrap gap-2 mt-6 text-sm font-mono" data-testid="route-meta">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10"><Gauge className="w-3.5 h-3.5" /> {route.difficulty}</span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10"><MapPin className="w-3.5 h-3.5" /> {route.stops_label || route.stops.length} stops</span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10"><RouteIcon className="w-3.5 h-3.5" /> {route.distance_km} km</span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10"><Clock className="w-3.5 h-3.5" /> {route.duration_text}</span>
                {route.access && <span data-testid="access-remaining" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F5F5F0] text-[#001A4D]"><Lock className="w-3.5 h-3.5" /> {remainingText(route.access.expires_at)}</span>}
              </div>
              {route.access && <p className="mt-3 text-xs text-[#F5F5F0]/55" data-testid="location-notice"><MapPin className="w-3 h-3 inline mr-1" />Your location is shared with Whangamatā Cycles while this map is open — health & safety only.</p>}
            </div>
            <div className="grid grid-cols-2 sm:flex gap-3 w-full sm:w-auto">
              <Button
                data-testid="navigation-toggle"
                onClick={() => setAutoNav(true)}
                className="h-14 px-4 sm:px-8 rounded-full bg-[#F5F5F0] text-[#001A4D] hover:bg-white text-base font-medium shadow-xl"
              >
                <Navigation className="w-5 h-5 mr-2" /> Navigate
              </Button>
              <Button
                data-testid="exit-route-btn"
                variant="outline"
                onClick={() => { stopAudio(); setRoute(null); setCode(""); setUnlocked(getUnlocked()); }}
                className="h-14 px-4 sm:px-6 rounded-full border-white/25 bg-transparent text-[#F5F5F0] hover:bg-white/10 hover:text-white"
              >
                Exit map
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Story card */}
          <div className="lg:col-span-7">
            <motion.div
              key={stop.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="bg-white rounded-2xl border border-black/5 overflow-hidden"
              data-testid="active-stop-card"
            >
              <div className="relative h-64 md:h-80 overflow-hidden bg-neutral-200">
                {mainImg && <img src={mainImg} alt={stop.name} className="w-full h-full object-cover" />}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <div className="absolute bottom-0 inset-x-0 p-6 text-white">
                  <div className="flex items-center gap-2 overline text-[#93C5FD]"><MapPin className="w-3.5 h-3.5" /> Stop {active + 1} of {route.stops.length}</div>
                  <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl mt-1 leading-tight">{stop.name}</h2>
                </div>
              </div>
              {gallery.length > 1 && (
                <div className="flex gap-2 px-6 pt-4" data-testid="stop-gallery">
                  {gallery.map((g, gi) => (
                    <button key={gi} data-testid={`gallery-thumb-${gi}`} onClick={() => setImgIdx(gi)} className={`h-14 w-14 rounded-lg overflow-hidden border-2 transition-colors ${gi === imgIdx ? "border-[#001A4D]" : "border-transparent opacity-70 hover:opacity-100"}`}>
                      <img src={g} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
              <div className="p-6">
                {/* Player + subtitles */}
                <div className="rounded-2xl bg-[#0A0A0A] text-[#F5F5F0] p-5" data-testid="story-player">
                  <div className="flex items-center gap-4">
                    <button
                      data-testid="play-narration-btn"
                      onClick={playNarration}
                      disabled={audioLoading}
                      className="w-16 h-16 rounded-full bg-[#F5F5F0] text-[#0A0A0A] flex items-center justify-center shrink-0 hover:bg-white transition-colors disabled:opacity-60"
                      aria-label={playing ? "Pause story" : "Play the story"}
                    >
                      {audioLoading ? <Loader2 className="w-6 h-6 animate-spin" /> : playing ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-0.5" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className="overline text-[#93C5FD]">{audioLoading ? "Loading narration…" : playing ? "Now playing" : "Voiceover"}</p>
                      <p className="font-serif text-xl truncate">{playing ? "Listen to the story of this place" : "Tap play to hear the story"}</p>
                    </div>
                    <button data-testid="subtitles-toggle" onClick={() => setSubsOn((v) => !v)} className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${subsOn ? "bg-[#2563EB] text-white" : "bg-white/10 text-white/60"}`} aria-label="Toggle subtitles">
                      {subsOn ? <Captions className="w-5 h-5" /> : <CaptionsOff className="w-5 h-5" />}
                    </button>
                  </div>
                  {playing && subsOn && (
                    <div className="mt-4 min-h-[64px] flex items-center" data-testid="subtitle-box">
                      <motion.p key={subtitle} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="text-lg md:text-xl leading-snug text-white/90" data-testid="subtitle-text">{subtitle}</motion.p>
                    </div>
                  )}
                  {playing && audioT.d > 0 && (
                    <div className="mt-4 h-1 rounded-full bg-white/15 overflow-hidden"><div className="h-full bg-[#2563EB] transition-[width]" style={{ width: `${Math.min(100, (audioT.t / audioT.d) * 100)}%` }} /></div>
                  )}
                </div>

                <button data-testid="read-story-toggle" onClick={() => setReadStory((v) => !v)} className="mt-4 inline-flex items-center gap-2 text-sm text-[#001A4D] hover:underline">
                  <BookOpen className="w-4 h-4" /> {readStory ? "Hide the written story" : "Read the story instead"}
                </button>
                {readStory && <p data-testid="story-text" className="text-[#0A0A0A]/70 leading-relaxed mt-3 text-[15px]">{stop.story}</p>}

                {!isLastStop ? (
                  <div className="mt-6 border border-[#001A4D]/15 bg-[#001A4D]/5 rounded-xl p-4" data-testid="directions-block">
                    <p className="overline text-[#001A4D] mb-1">Ride to stop {active + 2} · {kmText(routeLeg(route.path, stop, route.stops[active + 1]).km)}</p>
                    <p className="text-sm text-[#0A0A0A]/70 leading-relaxed mb-3">{route.nav_mode === "owner" ? "Open Navigate — Whangamatā Cycles' own directions are read aloud as you reach each spot." : (stop.directions || "Open Navigate for turn-by-turn directions to the next stop.")}</p>
                    <button data-testid="start-nav-btn" onClick={() => setAutoNav(true)} className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#001A4D] hover:bg-[#00287A] text-white text-sm font-medium transition-colors">
                      <Navigation className="w-4 h-4" /> Navigate to stop {active + 2}
                    </button>
                  </div>
                ) : (
                  <div className="mt-6 border border-[#001A4D]/15 bg-[#001A4D]/5 rounded-xl p-4" data-testid="directions-block">
                    <p className="overline text-[#001A4D] mb-1">Last stop · ride back to Whangamatā Cycles</p>
                    <p className="text-sm text-[#0A0A0A]/70 leading-relaxed mb-3">Every route finishes back at Whangamatā Cycles. Use Navigate to guide you there.</p>
                    <button data-testid="start-nav-btn" onClick={() => setAutoNav(true)} className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#001A4D] hover:bg-[#00287A] text-white text-sm font-medium transition-colors">
                      <Store className="w-4 h-4" /> Navigate back to Whangamatā Cycles
                    </button>
                  </div>
                )}

                <div className="flex gap-3 mt-6">
                  <Button data-testid="prev-stop-btn" variant="outline" disabled={active === 0} onClick={() => setActive((i) => Math.max(0, i - 1))} className="flex-1 rounded-full border-black/15 h-12">
                    <ChevronLeft className="w-4 h-4 mr-1" /> Previous
                  </Button>
                  {isLastStop ? (
                    <Button data-testid="finish-ride-btn" onClick={() => setShowSummary(true)} className="flex-1 rounded-full h-12 bg-[#0A0A0A] hover:bg-[#001A4D] text-white">
                      Finish ride <Share2 className="w-4 h-4 ml-1" />
                    </Button>
                  ) : (
                    <Button data-testid="next-stop-btn" onClick={() => setActive((i) => Math.min(route.stops.length - 1, i + 1))} className="flex-1 rounded-full h-12 bg-[#0A0A0A] hover:bg-[#001A4D] text-white">
                      Next stop <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Timeline + help */}
          <aside className="lg:col-span-5 space-y-6">
            <div className="bg-white rounded-2xl border border-black/5 p-6" data-testid="route-timeline">
              <p className="overline text-[#001A4D] mb-5">Your ride</p>
              <ol className="relative">
                <span className="absolute left-[15px] top-3 bottom-3 w-px bg-black/10" />
                <li className="relative flex items-center gap-4 pb-5" data-testid="timeline-start">
                  <span className="relative z-10 w-8 h-8 rounded-lg bg-[#0A0A0A] text-white flex items-center justify-center shrink-0"><Store className="w-4 h-4" /></span>
                  <div><p className="text-sm font-medium">Whangamatā Cycles</p><p className="text-xs text-black/50 font-mono">Start · leave the shop</p></div>
                </li>
                {route.stops.map((s, i) => {
                  const done = i < visitedMax || (i < active);
                  const isActive = i === active;
                  return (
                    <li key={s.id} className="relative flex items-center gap-4 pb-5" data-testid={`timeline-stop-${i}`}>
                      <span className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center shrink-0 font-mono text-xs font-bold border-2 ${isActive ? "bg-[#2563EB] border-[#2563EB] text-white shadow-[0_0_0_4px_rgba(37,99,235,0.2)]" : done ? "bg-[#001A4D] border-[#001A4D] text-white" : "bg-white border-black/15 text-black/50"}`}>
                        {done && !isActive ? <Check className="w-4 h-4" /> : i + 1}
                      </span>
                      <div className="min-w-0">
                        <p className={`text-sm truncate ${isActive ? "font-semibold text-[#0A0A0A]" : done ? "text-black/60" : "text-black/70"}`}>{s.name}</p>
                        <p className="text-xs text-black/45 font-mono">{isActive ? "You're here" : done ? "Visited" : i > 0 ? kmText(routeLeg(route.path, route.stops[i - 1], s).km).replace(" away", " from previous") : "First stop"}</p>
                      </div>
                    </li>
                  );
                })}
                <li className="relative flex items-center gap-4" data-testid="timeline-end">
                  <span className="relative z-10 w-8 h-8 rounded-lg bg-[#0A0A0A] text-white flex items-center justify-center shrink-0"><Store className="w-4 h-4" /></span>
                  <div><p className="text-sm font-medium">Whangamatā Cycles</p><p className="text-xs text-black/50 font-mono">Finish · back at the shop</p></div>
                </li>
              </ol>
            </div>

            <div className="rounded-2xl border border-[#001A4D]/15 bg-[#001A4D]/5 p-6" data-testid="help-card">
              <p className="overline text-[#001A4D] mb-2">Need help?</p>
              <p className="text-sm text-black/65 leading-relaxed mb-4">Taken a wrong turn or can't find the route? Tap below and we'll send someone out to find you and get you back on track.</p>
              <LostButton route={route} stopIndex={active} className="w-full" />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
